import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { TrashIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';

// Используем локальный worker (копируем с npm скрипт перед использованием)
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

type DrawMode = 'rectangle' | 'polygon';

interface Point {
  x: number;
  y: number;
}

interface Crop {
  id: string;
  type: 'rectangle' | 'polygon';
  // Для rectangle (нормализованные координаты 0-1):
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  // Для polygon (нормализованные координаты 0-1):
  points?: Point[];
  // Цвет для заливки (генерируется автоматически)
  color?: string;
}

interface PdfCropperViewerProps {
  pdfData: ArrayBuffer;
  onCropSubmit: (croppedPdfs: Blob[]) => Promise<void>;
  schemePageNumber: number; // Номер страницы из структуры pd_result
}

export interface PdfCropperViewerHandle {
  getCanvasWithMarkup: () => HTMLCanvasElement | null;
  getCanvasWithPdfAndMarkup: () => HTMLCanvasElement | null;
}

const PdfCropperViewer = forwardRef<PdfCropperViewerHandle, PdfCropperViewerProps>(
  ({ pdfData, onCropSubmit, schemePageNumber }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const overlayRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

  const [pdf, setPdf] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [isRendering, setIsRendering] = useState(false);
  
  // Режим рисования
  const [drawMode, setDrawMode] = useState<DrawMode>('rectangle');
  
  // Для прямоугольников
  const [isDrawing, setIsDrawing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [currentRect, setCurrentRect] = useState<Crop | null>(null);
  
  // Для полигонов
  const [polygonPoints, setPolygonPoints] = useState<Point[]>([]);
  const [isPolygonActive, setIsPolygonActive] = useState(false);
  
  // Общее
  const [crops, setCrops] = useState<Crop[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Для редактирования существующих прямоугольников и полигонов
  const [selectedCropId, setSelectedCropId] = useState<string | null>(null);
  const [dragMode, setDragMode] = useState<'none' | 'move' | 'resize' | 'vertex'>('none');
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartY, setDragStartY] = useState(0);
  const [originalCrop, setOriginalCrop] = useState<Crop | null>(null);
  
  // Для редактирования вершин полигона
  const [selectedVertexIndex, setSelectedVertexIndex] = useState<number | null>(null);

  // Загружаем PDF
  useEffect(() => {
    const loadPdf = async () => {
      try {
        // КРИТИЧНО: Создаем копию ArrayBuffer правильным способом
        // Worker "отсоединяет" (detaches) буфер, делая оригинал недоступным
        const pdfDataCopy = new Uint8Array(pdfData).buffer;
        
        const loadedPdf = await pdfjsLib.getDocument({ data: pdfDataCopy }).promise;
        setPdf(loadedPdf);
        setTotalPages(loadedPdf.numPages);
        renderPage(loadedPdf, 1);
      } catch (err) {
        console.error('Error loading PDF:', err);
      }
    };
    loadPdf();
  }, [pdfData]);

  const renderPage = async (pdfDoc: any, pageNum: number) => {
    if (isRendering) {
      console.log('Already rendering, skipping...');
      return; // Защита от двойного рендеринга
    }
    
    setIsRendering(true);
    try {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      
      if (canvasRef.current) {
        canvasRef.current.width = viewport.width;
        canvasRef.current.height = viewport.height;
        
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          await page.render({ canvasContext: ctx, viewport }).promise;
        }
      }

      if (overlayRef.current) {
        overlayRef.current.width = viewport.width;
        overlayRef.current.height = viewport.height;
        const ctx = overlayRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, viewport.width, viewport.height);
        }
      }
      
      // Перерисовываем crops после изменения размера canvas
      // Используем requestAnimationFrame чтобы дать время браузеру завершить рендер
      requestAnimationFrame(() => {
        redrawOverlay(crops);
      });
    } catch (err) {
      console.error('Error rendering page:', err);
    } finally {
      setIsRendering(false);
    }
  };

  useEffect(() => {
    if (pdf) {
      renderPage(pdf, currentPage);
    }
  }, [pdf, currentPage, scale]);

  // Экспортируем методы для получения canvas'ей
  useImperativeHandle(ref, () => ({
    getCanvasWithMarkup: () => overlayRef.current,
    getCanvasWithPdfAndMarkup: () => {
      // Создаём новый canvas с объединением PDF + разметки
      if (!canvasRef.current || !overlayRef.current) return null;
      
      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = canvasRef.current.width;
      combinedCanvas.height = canvasRef.current.height;
      
      const ctx = combinedCanvas.getContext('2d');
      if (!ctx) return null;
      
      // Рисуем сначала PDF
      ctx.drawImage(canvasRef.current, 0, 0);
      
      // Потом оверлей с разметкой
      ctx.drawImage(overlayRef.current, 0, 0);
      
      return combinedCanvas;
    }
  }));

  // Перерисовываем оверлей когда меняются crops
  useEffect(() => {
    redrawOverlay(crops);
  }, [crops, selectedCropId]);

  // Получаем текущие размеры canvas
  const getCanvasSize = (): { width: number; height: number } => {
    if (!canvasRef.current) return { width: 0, height: 0 };
    return {
      width: canvasRef.current.width,
      height: canvasRef.current.height,
    };
  };

  // Генерируем случайный приятный цвет для заливки
  const generateRandomColor = (): string => {
    const hue = Math.floor(Math.random() * 360);
    const saturation = 60 + Math.floor(Math.random() * 20); // 60-80%
    const lightness = 50 + Math.floor(Math.random() * 20); // 50-70%
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Конвертируем нормализованные координаты в пиксели canvas
  const normalizedToPixels = (crop: Crop): Crop => {
    const { width: canvasWidth, height: canvasHeight } = getCanvasSize();
    
    if (crop.type === 'rectangle') {
      return {
        ...crop,
        x: crop.x! * canvasWidth,
        y: crop.y! * canvasHeight,
        width: crop.width! * canvasWidth,
        height: crop.height! * canvasHeight,
      };
    } else {
      // polygon
      return {
        ...crop,
        points: crop.points!.map(p => ({
          x: p.x * canvasWidth,
          y: p.y * canvasHeight,
        })),
      };
    }
  };

  // Конвертируем пиксели canvas в нормализованные координаты
  const pixelsToNormalized = (crop: Crop): Crop => {
    const { width: canvasWidth, height: canvasHeight } = getCanvasSize();
    if (canvasWidth === 0 || canvasHeight === 0) return crop;
    
    if (crop.type === 'rectangle') {
      return {
        ...crop,
        x: crop.x! / canvasWidth,
        y: crop.y! / canvasHeight,
        width: crop.width! / canvasWidth,
        height: crop.height! / canvasHeight,
      };
    } else {
      // polygon
      return {
        ...crop,
        points: crop.points!.map(p => ({
          x: p.x / canvasWidth,
          y: p.y / canvasHeight,
        })),
      };
    }
  };

  // Проверяем попадание в хендл ресайза (углы и края) - в пиксельных координатах - только для прямоугольников
  const getResizeHandle = (x: number, y: number, cropPixels: Crop): string | null => {
    if (cropPixels.type !== 'rectangle') return null;
    
    const handleSize = 8;
    const { x: cx, y: cy, width, height } = cropPixels;

    // Углы
    if (Math.abs(x - cx!) < handleSize && Math.abs(y - cy!) < handleSize) return 'nw';
    if (Math.abs(x - (cx! + width!)) < handleSize && Math.abs(y - cy!) < handleSize) return 'ne';
    if (Math.abs(x - cx!) < handleSize && Math.abs(y - (cy! + height!)) < handleSize) return 'sw';
    if (Math.abs(x - (cx! + width!)) < handleSize && Math.abs(y - (cy! + height!)) < handleSize) return 'se';

    // Края
    if (Math.abs(x - cx!) < handleSize && y >= cy! && y <= cy! + height!) return 'w';
    if (Math.abs(x - (cx! + width!)) < handleSize && y >= cy! && y <= cy! + height!) return 'e';
    if (Math.abs(y - cy!) < handleSize && x >= cx! && x <= cx! + width!) return 'n';
    if (Math.abs(y - (cy! + height!)) < handleSize && x >= cx! && x <= cx! + width!) return 's';

    return null;
  };

  // Проверяем попадание внутрь crop - в пиксельных координатах
  const isInsideCrop = (x: number, y: number, cropPixels: Crop): boolean => {
    if (cropPixels.type === 'rectangle') {
      return x >= cropPixels.x! && x <= cropPixels.x! + cropPixels.width! && 
             y >= cropPixels.y! && y <= cropPixels.y! + cropPixels.height!;
    } else {
      // polygon - используем ray casting algorithm
      const points = cropPixels.points!;
      let inside = false;
      for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        const xi = points[i].x;
        const yi = points[i].y;
        const xj = points[j].x;
        const yj = points[j].y;
        
        const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      return inside;
    }
  };

  // Находим crop под курсором - конвертируем все в пиксели для проверки
  const getCropAtPoint = (x: number, y: number): Crop | null => {
    // Проверяем в обратном порядке (последние сверху)
    for (let i = crops.length - 1; i >= 0; i -= 1) {
      const cropPixels = normalizedToPixels(crops[i]);
      if (isInsideCrop(x, y, cropPixels)) {
        return crops[i];
      }
    }
    return null;
  };

  // Проверяем попадание в вершину полигона
  const getPolygonVertexAtPoint = (x: number, y: number, crop: Crop): number | null => {
    if (crop.type !== 'polygon' || !crop.points) return null;
    
    const cropPixels = normalizedToPixels(crop);
    const points = cropPixels.points!;
    const handleSize = 8; // Размер области вокруг вершины для клика
    
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      if (Math.abs(x - p.x) < handleSize && Math.abs(y - p.y) < handleSize) {
        return i;
      }
    }
    return null;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // В режиме полигона: если клик попал в существующий полигон — начинаем перемещение,
    // иначе добавляем точку в текущий рисуемый полигон (или начинаем новый)
    if (drawMode === 'polygon') {
      const clickedCrop = getCropAtPoint(x, y);
      if (clickedCrop?.type === 'polygon') {
        // Проверяем клик на вершину для редактирования
        const vertexIndex = getPolygonVertexAtPoint(x, y, clickedCrop);
        if (vertexIndex !== null) {
          // Начинаем редактирование вершины
          setDragMode('vertex');
          setSelectedCropId(clickedCrop.id);
          setSelectedVertexIndex(vertexIndex);
          setOriginalCrop({ ...clickedCrop });
          setDragStartX(x);
          setDragStartY(y);
          return;
        }
        
        // Начинаем перемещение существующего полигона
        setDragMode('move');
        setSelectedCropId(clickedCrop.id);
        setOriginalCrop({ ...clickedCrop });
        setDragStartX(x);
        setDragStartY(y);
        return;
      }

      // Иначе добавляем точку в текущий полигон
      const newPoint = { x, y };
      setPolygonPoints(prev => [...prev, newPoint]);
      setIsPolygonActive(true);
      redrawOverlay(crops);
      return;
    }
    
    // В режиме прямоугольника проверяем клик на существующий crop
    const clickedCrop = getCropAtPoint(x, y);
    
    if (clickedCrop) {
      const clickedCropPixels = normalizedToPixels(clickedCrop);
      // Проверяем клик на хендл ресайза (только для прямоугольников)
      const handle = getResizeHandle(x, y, clickedCropPixels);
      
      if (handle) {
        // Начинаем ресайз
        setDragMode('resize');
        setResizeHandle(handle);
        setSelectedCropId(clickedCrop.id);
        setOriginalCrop({ ...clickedCrop });
        setDragStartX(x);
        setDragStartY(y);
      } else {
        // Начинаем перемещение
        setDragMode('move');
        setSelectedCropId(clickedCrop.id);
        setOriginalCrop({ ...clickedCrop });
        setDragStartX(x);
        setDragStartY(y);
      }
    } else {
      // Начинаем рисовать новый прямоугольник
      setIsDrawing(true);
      setStartX(x);
      setStartY(y);
      setSelectedCropId(null);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!overlayRef.current) return;
    
    const rect = overlayRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Получаем размеры canvas для конвертации
    const { width: canvasWidth, height: canvasHeight } = getCanvasSize();

    // Режим полигона - показываем preview линии до курсора
    if (drawMode === 'polygon' && isPolygonActive && polygonPoints.length > 0) {
      redrawOverlay(crops);
      return;
    }

    // Рисуем новый прямоугольник
    if (isDrawing) {
      const width = Math.abs(x - startX);
      const height = Math.abs(y - startY);

      const newRect: Crop = {
        id: 'current',
        type: 'rectangle',
        x: Math.min(startX, x),
        y: Math.min(startY, y),
        width,
        height,
      };
      setCurrentRect(newRect);
      redrawOverlay([...crops, newRect]);
      return;
    }

    // Перемещаем crop
    if (dragMode === 'move' && selectedCropId && originalCrop) {
      const dx = x - dragStartX;
      const dy = y - dragStartY;
      
      const updatedCrops = crops.map(c => {
        if (c.id !== selectedCropId) return c;
        
        if (c.type === 'rectangle') {
          // Конвертируем delta в нормализованные координаты
          const normalizedDx = dx / canvasWidth;
          const normalizedDy = dy / canvasHeight;
          
          return {
            ...c,
            x: originalCrop.x! + normalizedDx,
            y: originalCrop.y! + normalizedDy,
          };
        } else {
          // polygon - перемещаем все точки
          const normalizedDx = dx / canvasWidth;
          const normalizedDy = dy / canvasHeight;
          
          return {
            ...c,
            points: originalCrop.points!.map(p => ({
              x: p.x + normalizedDx,
              y: p.y + normalizedDy,
            })),
          };
        }
      });
      setCrops(updatedCrops);
      return;
    }

    // Редактируем вершину полигона
    if (dragMode === 'vertex' && selectedCropId && originalCrop && selectedVertexIndex !== null) {
      const dx = x - dragStartX;
      const dy = y - dragStartY;
      const normalizedDx = dx / canvasWidth;
      const normalizedDy = dy / canvasHeight;

      const updatedCrops = crops.map(c => {
        if (c.id !== selectedCropId || c.type !== 'polygon') return c;
        
        const newPoints = [...(originalCrop.points || [])];
        if (selectedVertexIndex < newPoints.length) {
          newPoints[selectedVertexIndex] = {
            x: originalCrop.points![selectedVertexIndex].x + normalizedDx,
            y: originalCrop.points![selectedVertexIndex].y + normalizedDy,
          };
        }
        
        return { ...c, points: newPoints };
      });
      setCrops(updatedCrops);
      return;
    }

    // Ресайзим crop (только прямоугольники)
    if (dragMode === 'resize' && selectedCropId && originalCrop && resizeHandle && originalCrop.type === 'rectangle') {
      const dx = x - dragStartX;
      const dy = y - dragStartY;

      // Конвертируем delta в нормализованные координаты
      const normalizedDx = dx / canvasWidth;
      const normalizedDy = dy / canvasHeight;

      const updatedCrops = crops.map(c => {
        if (c.id !== selectedCropId || c.type !== 'rectangle') return c;

        let { x: nx, y: ny, width: nw, height: nh } = originalCrop;

        // Применяем изменения в зависимости от хендла
        if (resizeHandle.includes('n')) {
          ny = originalCrop.y! + normalizedDy;
          nh = originalCrop.height! - normalizedDy;
        }
        if (resizeHandle.includes('s')) {
          nh = originalCrop.height! + normalizedDy;
        }
        if (resizeHandle.includes('w')) {
          nx = originalCrop.x! + normalizedDx;
          nw = originalCrop.width! - normalizedDx;
        }
        if (resizeHandle.includes('e')) {
          nw = originalCrop.width! + normalizedDx;
        }

        // Минимальный размер в нормализованных координатах (10px)
        const minSizeNormalized = 10 / canvasWidth;
        if (nw! < minSizeNormalized) {
          nw = minSizeNormalized;
          nx = c.x;
        }
        if (nh! < minSizeNormalized) {
          nh = minSizeNormalized;
          ny = c.y;
        }

        return { ...c, x: nx, y: ny, width: nw, height: nh };
      });

      setCrops(updatedCrops);
      return;
    }

    // Обновляем курсор при наведении
    const hoveredCrop = getCropAtPoint(x, y);
    if (hoveredCrop) {
      if (hoveredCrop.type === 'polygon') {
        // Проверяем наведение на вершину
        const vertexIndex = getPolygonVertexAtPoint(x, y, hoveredCrop);
        if (vertexIndex !== null) {
          overlayRef.current.style.cursor = 'pointer';
        } else {
          overlayRef.current.style.cursor = 'move';
        }
      } else {
        const hoveredCropPixels = normalizedToPixels(hoveredCrop);
        const handle = getResizeHandle(x, y, hoveredCropPixels);
        if (handle) {
          // Курсоры для ресайза
          const cursors: Record<string, string> = {
            nw: 'nw-resize',
            ne: 'ne-resize',
            sw: 'sw-resize',
            se: 'se-resize',
            n: 'n-resize',
            s: 's-resize',
            w: 'w-resize',
            e: 'e-resize',
          };
          overlayRef.current.style.cursor = cursors[handle] || 'move';
        } else {
          overlayRef.current.style.cursor = 'move';
        }
      }
    } else {
      overlayRef.current.style.cursor = 'crosshair';
    }
  };

  const handleMouseUp = () => {
    // Завершаем рисование нового прямоугольника
    if (isDrawing) {
      if (currentRect && currentRect.width! >= 10 && currentRect.height! >= 10) {
        // Конвертируем пиксельные координаты в нормализованные
        const normalizedCrop = pixelsToNormalized(currentRect);
        const newCrop = {
          ...normalizedCrop,
          id: Math.random().toString(36),
          color: generateRandomColor() // Добавляем случайный цвет
        };
        setCrops([...crops, newCrop]);
      }
      setIsDrawing(false);
      setCurrentRect(null);
      redrawOverlay(crops);
    }

    // Завершаем перемещение/ресайз/редактирование вершины
    if (dragMode !== 'none') {
      setDragMode('none');
      setResizeHandle(null);
      setOriginalCrop(null);
      setSelectedVertexIndex(null);
    }
  };

  const handleDoubleClick = () => {
    // Завершаем полигон при двойном клике
    if (drawMode === 'polygon' && polygonPoints.length >= 3) {
      const polygonCrop: Crop = {
        id: Math.random().toString(36),
        type: 'polygon',
        points: polygonPoints,
        color: generateRandomColor() // Добавляем случайный цвет
      };
      // Конвертируем в нормализованные координаты
      const normalizedCrop = pixelsToNormalized(polygonCrop);
      setCrops([...crops, normalizedCrop]);
      setPolygonPoints([]);
      setIsPolygonActive(false);
      redrawOverlay(crops);
    }
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Завершаем полигон при нажатии Enter или Escape
    if (drawMode === 'polygon' && isPolygonActive) {
      if (e.key === 'Enter' && polygonPoints.length >= 3) {
        // Завершаем полигон
        const polygonCrop: Crop = {
          id: Math.random().toString(36),
          type: 'polygon',
          points: polygonPoints,
          color: generateRandomColor()
        };
        const normalizedCrop = pixelsToNormalized(polygonCrop);
        setCrops(prev => [...prev, normalizedCrop]);
        setPolygonPoints([]);
        setIsPolygonActive(false);
      } else if (e.key === 'Escape') {
        setPolygonPoints([]);
        setIsPolygonActive(false);
      }
    }
  }, [drawMode, isPolygonActive, polygonPoints]);

  // Слушаем клавиатуру для завершения полигона
  useEffect(() => {
    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const redrawOverlay = (allCrops: Crop[]) => {
    if (!overlayRef.current) return;
    const ctx = overlayRef.current.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, overlayRef.current.width, overlayRef.current.height);

    // Рисуем завершённые crops (конвертируем из нормализованных в пиксели)
    const finishedCrops = allCrops.filter(c => c.id !== 'current');
    for (let idx = 0; idx < finishedCrops.length; idx += 1) {
      const cropNormalized = finishedCrops[idx];
      const crop = normalizedToPixels(cropNormalized); // Конвертируем в пиксели для отрисовки
      const isSelected = cropNormalized.id === selectedCropId;
      
      ctx.strokeStyle = isSelected ? '#3b82f6' : '#ef4444';
      ctx.lineWidth = isSelected ? 3 : 2;
      
      if (crop.type === 'rectangle') {
        // Полупрозрачная заливка с цветом региона
        if (crop.color) {
          // Конвертируем цвет в rgba с прозрачностью
          const rgbaColor = crop.color.replace('hsl', 'hsla').replace(')', ', 0.15)');
          ctx.fillStyle = rgbaColor;
          ctx.fillRect(crop.x!, crop.y!, crop.width!, crop.height!);
        }
        
        // Рамка прямоугольника
        ctx.strokeRect(crop.x!, crop.y!, crop.width!, crop.height!);
        
        // Номер
        ctx.fillStyle = isSelected ? '#3b82f6' : '#ef4444';
        ctx.font = 'bold 14px Arial';
        ctx.fillText(String(idx + 1), crop.x! + 5, crop.y! + 20);

        // Хендлы для выбранного crop
        if (isSelected) {
          const handleSize = 6;
          ctx.fillStyle = '#3b82f6';
          
          // Углы
          ctx.fillRect(crop.x! - handleSize / 2, crop.y! - handleSize / 2, handleSize, handleSize);
          ctx.fillRect(crop.x! + crop.width! - handleSize / 2, crop.y! - handleSize / 2, handleSize, handleSize);
          ctx.fillRect(crop.x! - handleSize / 2, crop.y! + crop.height! - handleSize / 2, handleSize, handleSize);
          ctx.fillRect(crop.x! + crop.width! - handleSize / 2, crop.y! + crop.height! - handleSize / 2, handleSize, handleSize);
          
          // Середины сторон
          ctx.fillRect(crop.x! + crop.width! / 2 - handleSize / 2, crop.y! - handleSize / 2, handleSize, handleSize);
          ctx.fillRect(crop.x! + crop.width! / 2 - handleSize / 2, crop.y! + crop.height! - handleSize / 2, handleSize, handleSize);
          ctx.fillRect(crop.x! - handleSize / 2, crop.y! + crop.height! / 2 - handleSize / 2, handleSize, handleSize);
          ctx.fillRect(crop.x! + crop.width! - handleSize / 2, crop.y! + crop.height! / 2 - handleSize / 2, handleSize, handleSize);
        }
      } else {
        // Полигон
        const points = crop.points!;
        if (points.length >= 2) {
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
          }
          ctx.closePath();
          ctx.stroke();
          
          // Полупрозрачная заливка с цветом региона
          if (crop.color) {
            const rgbaColor = crop.color.replace('hsl', 'hsla').replace(')', ', 0.15)');
            ctx.fillStyle = rgbaColor;
            ctx.fill();
          }
          
          // Номер в центре полигона
          const centerX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
          const centerY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
          ctx.fillStyle = isSelected ? '#3b82f6' : '#ef4444';
          ctx.font = 'bold 14px Arial';
          ctx.fillText(String(idx + 1), centerX - 5, centerY + 5);
          
          // Точки вершин
          const pointSize = isSelected ? 6 : 4;
          ctx.fillStyle = isSelected ? '#3b82f6' : '#ef4444';
          for (let i = 0; i < points.length; i++) {
            const point = points[i];
            // Выделяем выбранную вершину
            if (isSelected && selectedVertexIndex === i) {
              ctx.fillStyle = '#10b981'; // Зеленый для активной вершины
              ctx.fillRect(point.x - pointSize, point.y - pointSize, pointSize * 2, pointSize * 2);
              ctx.fillStyle = '#3b82f6';
            } else {
              ctx.fillRect(point.x - pointSize / 2, point.y - pointSize / 2, pointSize, pointSize);
            }
          }
        }
      }
    }

    // Preview текущего нового прямоугольника
    if (currentRect && currentRect.width! > 5) {
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(currentRect.x!, currentRect.y!, currentRect.width!, currentRect.height!);
      ctx.setLineDash([]);
    }
    
    // Preview текущего полигона
    if (isPolygonActive && polygonPoints.length > 0) {
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(polygonPoints[0].x, polygonPoints[0].y);
      for (let i = 1; i < polygonPoints.length; i++) {
        ctx.lineTo(polygonPoints[i].x, polygonPoints[i].y);
      }
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Точки красного цвета
      ctx.fillStyle = '#ef4444'; // Красный цвет для всех точек
      for (const point of polygonPoints) {
        ctx.fillRect(point.x - 3, point.y - 3, 6, 6);
      }
      
      // Первая точка большего размера чтобы показать где начало (тоже красная)
      if (polygonPoints.length > 0) {
        ctx.fillStyle = '#ef4444'; // Красный
        ctx.fillRect(polygonPoints[0].x - 5, polygonPoints[0].y - 5, 10, 10);
      }
    }
  };

  const handleDeleteCrop = (id: string) => {
    const updated = crops.filter(c => c.id !== id);
    setCrops(updated);
    redrawOverlay(updated);
  };

  const handleSubmitCrops = async () => {
    if (crops.length === 0) {
      alert('Выделите хотя бы один регион!');
      return;
    }

    setIsSubmitting(true);
    try {
      const croppedBlobs: Blob[] = [];

      for (const crop of crops) {
        // ИЗМЕНЕНО: Теперь оба типа кропа используют растровый подход
        // Это решает проблему с PDF, содержащими только изображения
        if (crop.type === 'rectangle') {
          const blob = await cropRectangleWithCanvas(crop);
          croppedBlobs.push(blob);
        } else {
          const blob = await cropPolygonWithCanvas(crop);
          croppedBlobs.push(blob);
        }
      }

      await onCropSubmit(croppedBlobs);
      setCrops([]);
      setPolygonPoints([]);
      setIsPolygonActive(false);
      redrawOverlay([]);
    } catch (err) {
      console.error('Error submitting crops:', err);
      alert('Ошибка при обработке!');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Кроп прямоугольника через Canvas + jsPDF (растровый подход)
  const cropRectangleWithCanvas = async (crop: Crop): Promise<Blob> => {
    if (!pdf) throw new Error('PDF not loaded');
    
    // Рендерим PDF на canvas в хорошем разрешении
    const highScale = 2;
    const page = await pdf.getPage(currentPage);
    const viewport = page.getViewport({ scale: highScale });
    
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = viewport.width;
    tempCanvas.height = viewport.height;
    const tempCtx = tempCanvas.getContext('2d')!;
    await page.render({ canvasContext: tempCtx, viewport }).promise;
    
    // Конвертируем нормализованные координаты в пиксели высокого разрешения
    const x = crop.x! * viewport.width;
    const y = crop.y! * viewport.height;
    const width = crop.width! * viewport.width;
    const height = crop.height! * viewport.height;
    
    // Создаём canvas для обрезанного результата
    const resultCanvas = document.createElement('canvas');
    resultCanvas.width = width;
    resultCanvas.height = height;
    const resultCtx = resultCanvas.getContext('2d')!;
    
    // Заливаем белым фоном
    resultCtx.fillStyle = '#FFFFFF';
    resultCtx.fillRect(0, 0, width, height);
    
    // Копируем нужную область из PDF
    resultCtx.drawImage(tempCanvas, x, y, width, height, 0, 0, width, height);
    
    // Конвертируем canvas в PDF через jsPDF
    const imgData = resultCanvas.toDataURL('image/jpeg', 1); // JPEG 100% качество
    const pdfDoc = new jsPDF({
      orientation: width > height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [width, height],
    });
    
    pdfDoc.addImage(imgData, 'JPEG', 0, 0, width, height);
    
    return pdfDoc.output('blob');
  };

  // Кроп полигона через Canvas + jsPDF (растеризация)
  const cropPolygonWithCanvas = async (crop: Crop): Promise<Blob> => {
    if (!pdf) throw new Error('PDF not loaded');
    
    // Рендерим PDF на canvas в хорошем разрешении (баланс качество/размер)
    const highScale = 2; // Средне-высокое качество (было 3, уменьшили для меньшего размера файла)
    const page = await pdf.getPage(currentPage);
    const viewport = page.getViewport({ scale: highScale });
    
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = viewport.width;
    tempCanvas.height = viewport.height;
    const tempCtx = tempCanvas.getContext('2d')!;
    await page.render({ canvasContext: tempCtx, viewport }).promise;
    
    // Конвертируем нормализованные точки в пиксели высокого разрешения
    const points = crop.points!.map(p => ({
      x: p.x * viewport.width,
      y: p.y * viewport.height,
    }));
    
    // Находим bounding box полигона
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    const width = maxX - minX;
    const height = maxY - minY;
    
    // Создаём canvas для обрезанного результата
    const resultCanvas = document.createElement('canvas');
    resultCanvas.width = width;
    resultCanvas.height = height;
    const resultCtx = resultCanvas.getContext('2d')!;
    
    // ВАЖНО: Заливаем белым фоном (JPEG не поддерживает прозрачность)
    resultCtx.fillStyle = '#FFFFFF';
    resultCtx.fillRect(0, 0, width, height);
    
    // Создаём полигональную маску
    resultCtx.save();
    resultCtx.beginPath();
    resultCtx.moveTo(points[0].x - minX, points[0].y - minY);
    for (let i = 1; i < points.length; i++) {
      resultCtx.lineTo(points[i].x - minX, points[i].y - minY);
    }
    resultCtx.closePath();
    resultCtx.clip();
    
    // Рисуем PDF в обрезанном canvas с учётом смещения
    resultCtx.drawImage(tempCanvas, -minX, -minY);
    resultCtx.restore();
    
    // Конвертируем canvas в PDF через jsPDF
    const imgData = resultCanvas.toDataURL('image/jpeg', 1); // JPEG 100% качество
    const pdfDoc = new jsPDF({
      orientation: width > height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [width, height],
    });
    
    pdfDoc.addImage(imgData, 'JPEG', 0, 0, width, height);
    
    return pdfDoc.output('blob');
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Toolbar - ФИКСИРОВАННЫЙ */}
      <div className="flex-shrink-0 flex items-center justify-between bg-white p-3 rounded border border-gray-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className="p-2 hover:bg-gray-100 disabled:opacity-50 rounded"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <span className="text-sm text-gray-600 whitespace-nowrap">
            Страница {currentPage} из {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="p-2 hover:bg-gray-100 disabled:opacity-50 rounded"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Переключатель режимов */}
        <div className="flex items-center gap-2 bg-gray-50 px-2 py-1 rounded border border-gray-200">
          <button
            onClick={() => {
              setDrawMode('rectangle');
              setPolygonPoints([]);
              setIsPolygonActive(false);
              redrawOverlay(crops);
            }}
            className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1 ${
              drawMode === 'rectangle' 
                ? 'bg-blue-500 text-white shadow-sm' 
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="4" y="4" width="16" height="16" strokeWidth="2" />
            </svg>
            Прямоугольник
          </button>
          <button
            onClick={() => {
              setDrawMode('polygon');
              setIsDrawing(false);
              setCurrentRect(null);
            }}
            className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1 ${
              drawMode === 'polygon' 
                ? 'bg-blue-500 text-white shadow-sm' 
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M12 2 L22 8 L18 20 L6 20 L2 8 Z" strokeWidth="2" />
            </svg>
            Полигон
          </button>
        </div>

        <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded border border-gray-200">
          <button
            onClick={() => setScale(Math.max(0.5, scale - 0.25))}
            className="px-2 py-1 hover:bg-gray-200 rounded text-sm font-medium"
          >
            −
          </button>
          <input
            type="range"
            min="0.5"
            max="3"
            step="0.1"
            value={scale}
            onChange={(e) => setScale(Number.parseFloat(e.target.value))}
            className="w-32"
          />
          <button
            onClick={() => setScale(Math.min(3, scale + 0.25))}
            className="px-2 py-1 hover:bg-gray-200 rounded text-sm font-medium"
          >
            +
          </button>
          <span className="text-sm text-gray-600 w-14 text-center bg-white px-2 py-1 rounded border border-gray-300">
            {Math.round(scale * 100)}%
          </span>
        </div>

        <button
          onClick={handleSubmitCrops}
          disabled={isSubmitting || crops.length === 0}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 flex items-center gap-2 text-sm whitespace-nowrap"
        >
          <CheckIcon className="h-4 w-4" />
          Обработать ({crops.length})
        </button>
      </div>
      
      {/* Подсказка для полигонального режима */}
      {drawMode === 'polygon' && (
        <div className="flex-shrink-0 bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-700">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className="font-medium mb-1">Режим полигонального выделения:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Кликайте мышью чтобы добавить точки полигона</li>
                <li>Двойной клик или <kbd className="px-1 py-0.5 bg-blue-100 rounded">Enter</kbd> - завершить полигон (минимум 3 точки)</li>
                <li><kbd className="px-1 py-0.5 bg-blue-100 rounded">Esc</kbd> - отменить текущий полигон</li>
                <li>Клик на вершину готового полигона - перемещение вершины для изменения формы</li>
                <li>Клик внутри полигона - перемещение всей фигуры</li>
              </ul>
            </div>
            {isPolygonActive && polygonPoints.length >= 3 && (
              <button
                onClick={handleDoubleClick}
                className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs font-medium whitespace-nowrap flex items-center gap-1"
              >
                <CheckIcon className="h-4 w-4" />
                Завершить полигон
              </button>
            )}
          </div>
        </div>
      )}

      {/* Подсказка для режима прямоугольника */}
      {drawMode === 'rectangle' && (
        <div className="flex-shrink-0 bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
          <p className="font-semibold mb-1">✅ Рекомендуется: использовать режим Прямоугольник</p>
          <p className="mb-1 text-xs">Используйте прямоугольник в первую очередь. Полигон только если прямоугольник не позволяет корректно выделить область.</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>Нажмите и тяните, чтобы нарисовать прямоугольник</li>
            <li>Отпустите мышь, чтобы завершить. Затем можно перетаскивать и изменять размер через хендлы</li>
            <li>Клик по уже созданной области — выделить и переместить</li>
            <li>Результат: JPEG файл (~2-3 МБ), работает с любыми PDF (векторными и растровыми)</li>
          </ul>
        </div>
      )}

      {/* Canvas - ФИКСИРОВАННАЯ ОБЛАСТЬ С OVERFLOW */}
      <div className="flex-1 relative overflow-hidden bg-gray-100 rounded border border-gray-300">
        <div className="absolute inset-0 overflow-auto p-4">
          <div
            ref={containerRef}
            className="inline-block relative"
          >
            <canvas
              ref={canvasRef}
              className="border border-gray-400 bg-white block"
            />
            <canvas
              ref={overlayRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onDoubleClick={handleDoubleClick}
              className="absolute top-0 left-0 cursor-crosshair"
              style={{ pointerEvents: 'auto' }}
            />
          </div>
        </div>
      </div>

      {/* Crops list */}
      {crops.length > 0 && (
        <div className="bg-white p-3 rounded border border-gray-200 max-h-24 overflow-y-auto">
          <div className="space-y-2">
            {crops.map((crop, idx) => {
              // Конвертируем нормализованные координаты в пиксели для отображения
              const cropPixels = normalizedToPixels(crop);
              const displayText = crop.type === 'rectangle' 
                ? `Прямоугольник ${idx + 1}: ${Math.round(cropPixels.width!)} × ${Math.round(cropPixels.height!)}px`
                : `Полигон ${idx + 1}: ${crop.points!.length} точек`;
              
              return (
                <div
                  key={crop.id}
                  className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm"
                >
                  <span className="text-gray-700 flex items-center gap-2">
                    {crop.type === 'rectangle' ? (
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <rect x="4" y="4" width="16" height="16" strokeWidth="2" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2 L22 8 L18 20 L6 20 L2 8 Z" strokeWidth="2" />
                      </svg>
                    )}
                    {displayText}
                  </span>
                  <button
                    onClick={() => handleDeleteCrop(crop.id)}
                    className="p-1 hover:bg-red-100 rounded"
                  >
                    <TrashIcon className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
  }
);

PdfCropperViewer.displayName = 'PdfCropperViewer';

export default PdfCropperViewer;
